import { useCallback } from "react";

import { PlayStateApi } from "../use-play-state-api";
import { useAddStencil } from "./use-add-stencil";
import { useAddStencils } from "./use-add-stencils";
import { useRemoveStencil } from "./use-remove-stencil";
import { useUpdateActionType } from "./use-update-action-type";
import { useUpdateFrameStencils } from "./use-update-frame-stencils";
import { useUpdateMovementTime } from "./use-update-movement-time";
import { useUpdatePossession } from "./use-update-possession";
import { useUpdateStencilColor } from "./use-update-stencil-color";
import { useUpdateStencilControlPoints } from "./use-update-stencil-control-points";
import { useUpdateStencilPosition } from "./use-update-stencil-position";
import { useUpdateStencilRotation } from "./use-update-stencil-rotation";

export const useFrameStencilsApi = ({ updateActiveFrame }: Pick<PlayStateApi, "updateActiveFrame">) => {
  const updateFrameStencils = useUpdateFrameStencils({ updateActiveFrame });
  const addStencil = useAddStencil(updateFrameStencils);
  const addStencils = useAddStencils(updateFrameStencils);
  const updateStencilPosition = useUpdateStencilPosition(updateFrameStencils);
  const updatePossession = useUpdatePossession(updateFrameStencils);
  const updateActionType = useUpdateActionType(updateFrameStencils);
  const updateStencilControlPoints = useUpdateStencilControlPoints(updateFrameStencils);
  const removeStencil = useRemoveStencil(updateFrameStencils);
  const updateMovementTime = useUpdateMovementTime(updateFrameStencils);
  const updateStencilColor = useUpdateStencilColor(updateFrameStencils);
  const updateStencilRotation = useUpdateStencilRotation(updateFrameStencils);

  /** ======== Clear Stencils ======== */
  const clearStencils = useCallback(() => {
    updateFrameStencils([]);
  }, [updateFrameStencils]);

  return {
    updateFrameStencils,
    addStencil,
    addStencils,
    updateStencilPosition,
    updatePossession,
    updateActionType,
    updateStencilControlPoints,
    removeStencil,
    updateMovementTime,
    clearStencils,
    updateStencilColor,
    updateStencilRotation,
  };
};

export type FrameStencilsApi = ReturnType<typeof useFrameStencilsApi>;

export const DEFAULT_FRAME_STENCILS_API: FrameStencilsApi = {
  updateFrameStencils: () => undefined,
  addStencil: () => undefined,
  addStencils: () => undefined,
  updateStencilPosition: () => undefined,
  updatePossession: () => undefined,
  updateActionType: () => undefined,
  updateStencilControlPoints: () => undefined,
  removeStencil: () => undefined,
  updateMovementTime: () => undefined,
  clearStencils: () => undefined,
  updateStencilColor: () => undefined,
  updateStencilRotation: () => undefined,
};
